export interface Solution {
  slug: string
  industry: string
  headline: string
  description: string
  heroDescription: string
  // Optional SEO overrides — when set, they replace the dynamically-derived
  // <title> and <meta description> for this industry's detail page.
  // Fall back to `${industry} — Document Extraction` / `description` when
  // omitted so any solution added without SEO copy still ships sane defaults.
  seoTitle?: string
  seoDescription?: string
  painPoints: { title: string; description: string }[]
  features: { title: string; description: string }[]
  caseStudySlugs: string[]
  /**
   * Slugs from src/data/functions.ts — drives the "Functions we cover"
   * cross-link section on each industry detail page and ensures every
   * industry has discoverable links into the function pages it shares
   * with other industries.
   */
  functionSlugs: string[]
  stats: { value: string; label: string }[]
  relatedLinks: { title: string; url: string; description: string }[]
}

export const solutions: Solution[] = [
  {
    slug: 'manufacturing',
    industry: 'Manufacturing',
    headline: 'Turn supplier documents into structured data without manual entry',
    description:
      'DocumentIQ helps manufacturing teams digitize invoices, spec sheets, and quality certificates at scale — eliminating transcription errors and freeing engineers from data entry.',
    seoTitle: 'AI Manufacturing Document Management | Algoscale',
    seoDescription:
      'Automate manufacturing workflows with AI-powered Enterprise Document Management. Extract invoices, purchase orders, BOMs, and quality documents faster.',
    heroDescription:
      'Manufacturing procurement and quality teams drown in paper: supplier invoices in four different formats, spec sheets with complex BOMs, and mill test certificates that must be verified before shipment. DocumentIQ extracts structured data from all of them in minutes, not days. Configure your fields once, annotate a handful of examples, and let AI handle the rest — with a feedback loop that gets smarter with every correction.',
    painPoints: [
      {
        title: 'Manual data entry from supplier documents',
        description:
          'AP clerks and engineers spend hours re-keying data from invoices, spec sheets, and purchase orders into ERP and PLM systems — work that is slow, repetitive, and error-prone.',
      },
      {
        title: 'Varied invoice and document formats',
        description:
          'Hundreds of vendors send documents in wildly different layouts. Traditional OCR and template-based tools break when a new format arrives, requiring constant reconfiguration.',
      },
      {
        title: 'BOM transcription errors',
        description:
          'Miskeyed part numbers, wrong material grades, and transposed quantities from bill of materials lead to incorrect parts on the production floor, causing costly rework and delays.',
      },
      {
        title: 'Quality certificate review bottleneck',
        description:
          'Every incoming shipment requires manual review of mill test certificates against specifications. The quality team can not keep pace with volume, delaying shipments and occasionally letting out-of-spec material through.',
      },
    ],
    features: [
      {
        title: 'Multi-row extraction',
        description:
          'Automatically extract line items from invoices, BOMs, and composition tables — even when tables span multiple pages or use inconsistent column headers.',
      },
      {
        title: 'Annotations for format variety',
        description:
          'Annotate a handful of representative documents per format to teach the AI where data appears. No template programming — just draw boxes and the model learns the pattern.',
      },
      {
        title: 'Feedback loops',
        description:
          'When extraction misses an edge case, correct it inline and re-extract. Corrections become ground-truth examples that improve accuracy on subsequent documents from the same source.',
      },
      {
        title: 'Batch processing',
        description:
          'Upload thousands of documents and process them in parallel. Real-time progress tracking lets you monitor the job without blocking your workflow.',
      },
    ],
    caseStudySlugs: [
      'invoice-digitization',
      'bill-of-materials-extraction',
      'quality-certificate-compliance',
    ],
    functionSlugs: ['ap-automation', 'quality-compliance', 'customs-trade-compliance', 'contract-intelligence'],
    stats: [
      { value: '15,000+', label: 'Invoices digitized' },
      { value: '68%', label: 'Fewer transcription errors' },
      { value: '98%', label: 'Extraction accuracy after feedback' },
    ],
    relatedLinks: [
      { title: 'Generative AI in Manufacturing', url: 'https://algoscale.com/generative-ai-in-manufacturing/', description: 'How AI is transforming manufacturing operations and supply chains' },
      { title: 'Data Engineering Services', url: 'https://algoscale.com/data-engineering-services/', description: 'Build robust data pipelines to feed your data lake' },
      { title: 'Data Lake Consulting', url: 'https://algoscale.com/data-lake-consulting-services/', description: 'Design and implement enterprise data lakes for structured document data' },
      { title: 'AI Consulting Services', url: 'https://algoscale.com/ai-consulting-service/', description: 'End-to-end AI strategy and implementation for enterprise' },
    ],
  },
  {
    slug: 'logistics',
    industry: 'Logistics',
    headline: 'Process shipping documents at the speed your supply chain demands',
    description:
      'DocumentIQ automates extraction from bills of lading, customs declarations, and receiving docs — eliminating data entry backlogs and cutting clearance times.',
    seoTitle: 'AI Logistics Document Processing | Algoscale',
    seoDescription:
      'Streamline logistics with AI-powered Enterprise Document Management. Automate bills of lading, customs documents, and shipping records with Algoscale.',
    heroDescription:
      'Logistics operations run on documents: bills of lading from dozens of carriers, customs declarations across 15+ countries, and receiving paperwork at every warehouse dock. Every hour spent re-keying shipment data is an hour a shipment sits waiting. DocumentIQ extracts structured data from all of these document types in a single pipeline, with carrier-specific annotations that handle format differences automatically and real-time processing that keeps pace with daily volume.',
    painPoints: [
      {
        title: 'Carrier-specific BOL formats',
        description:
          'Every carrier has its own bill of lading layout. The same fields — shipper, consignee, weight, piece count — appear in different positions on every template, breaking generic extraction tools.',
      },
      {
        title: 'Customs paperwork volume',
        description:
          'Each clearance requires data from 3-5 documents: commercial invoices with tariff line items, packing lists, certificates of origin. Brokers spend more time on data extraction than on actual compliance work.',
      },
      {
        title: 'Warehouse receiving backlog',
        description:
          'Inbound shipments arrive faster than teams can key receiving data into the WMS. Multi-day lags between physical receipt and system visibility create inventory discrepancies and fulfillment errors.',
      },
      {
        title: 'Manual TMS data entry',
        description:
          'Operations teams manually enter shipment details into transportation management systems. At 500+ shipments per day, even a small backlog cascades into detention charges and missed delivery windows.',
      },
    ],
    features: [
      {
        title: 'Single-pass extraction across document types',
        description:
          'Configure one field set that covers BOLs, commercial invoices, packing lists, and customs declarations. DocumentIQ extracts the right data from the right document type without separate configurations.',
      },
      {
        title: 'Annotations for carrier templates',
        description:
          'Create carrier-specific or client-specific annotation sets that teach the AI where data appears on each template. Add a new carrier by annotating 5-10 of their documents — no code changes needed.',
      },
      {
        title: 'Chat for operational queries',
        description:
          'Ask natural language questions across your shipment data: "Which shipments to Dallas exceed 10,000 lbs this week?" or "List all entries with HS code 8471 from the last 30 days" — no report builder needed.',
      },
      {
        title: 'Real-time processing',
        description:
          'WebSocket-powered progress tracking shows extraction status document by document. Process hundreds of shipment documents and see results as they complete, not after the entire batch finishes.',
      },
    ],
    caseStudySlugs: [
      'shipping-document-processing',
      'warehouse-receiving-automation',
      'customs-declaration-extraction',
    ],
    functionSlugs: ['customs-trade-compliance', 'ap-automation'],
    stats: [
      { value: '500+', label: 'Daily shipments processed' },
      { value: '60%', label: 'Faster customs clearance' },
      { value: '4.2% → 1.1%', label: 'Inventory discrepancy rate' },
    ],
    relatedLinks: [
      { title: 'Big Data Services', url: 'https://algoscale.com/data/big-data-services-company/', description: 'Handle massive document volumes with scalable data infrastructure' },
      { title: 'Data Integration Consulting', url: 'https://algoscale.com/data-integration-consulting/', description: 'Connect extracted data to your TMS, WMS, and ERP systems' },
      { title: 'AI Agent Development', url: 'https://algoscale.com/ai-agent-development-company/', description: 'Build intelligent agents that automate logistics workflows' },
      { title: 'Cloud Migration Services', url: 'https://algoscale.com/cloud-migration-services/', description: 'Move your document processing to the cloud' },
    ],
  },
  {
    slug: 'professional-services',
    industry: 'Professional Services',
    headline: 'Extract critical clauses from thousands of contracts without reading every page',
    description:
      'DocumentIQ parses contract libraries at scale — surfacing escalation clauses, renewal dates, and key terms that would take weeks to find manually.',
    seoTitle: 'Professional Services Document Management | Algoscale',
    seoDescription:
      'Transform document-intensive workflows with Enterprise Document Management. AI extracts contract data, reduces manual work, and improves efficiency.',
    heroDescription:
      'Professional services firms manage contract portfolios that grow faster than legal teams can review them. Price escalation clauses trigger without warning, renewal dates pass unnoticed, and critical terms stay buried in legal language across inconsistent templates. DocumentIQ extracts structured data from long-form contracts with high accuracy, lets you annotate clause patterns the AI should learn, and provides a chat interface for analytical queries across your entire contract library.',
    painPoints: [
      {
        title: 'Contract clauses buried in legal language',
        description:
          'Price escalation triggers, liability caps, termination conditions, and SLA terms are embedded deep in contracts — often in schedules or appendices rather than the main body. Finding them requires reading every page.',
      },
      {
        title: 'Missed renewal and escalation dates',
        description:
          'Without a systematic way to surface upcoming dates, procurement and legal teams discover escalation triggers and auto-renewals after the fact — when it is too late to negotiate or opt out.',
      },
      {
        title: 'Inconsistent contract templates',
        description:
          'Contracts from different counterparties, jurisdictions, and time periods use different structures and terminology for the same concepts, making keyword search unreliable.',
      },
      {
        title: 'Manual review does not scale',
        description:
          'A team that can carefully review 5 contracts per day cannot keep up with a portfolio of 1,200+. Backlogs grow, and the most critical contracts are not always the ones reviewed first.',
      },
    ],
    features: [
      {
        title: 'Single-pass extraction for long documents',
        description:
          'Extract dozens of fields from multi-page contracts in a single LLM pass. DocumentIQ handles documents with 50+ pages without losing accuracy on fields that appear late in the document.',
      },
      {
        title: 'Annotations for clause patterns',
        description:
          'Annotate where escalation clauses, renewal terms, or liability caps appear in a representative set of contracts. The AI uses these as few-shot examples to find similar clauses in new documents.',
      },
      {
        title: 'Chat for analytical queries',
        description:
          'Ask questions across your extracted contract data: "Which contracts have escalations triggering in Q3?" or "Show all agreements with liability caps under $1M" — and get cited, document-specific answers.',
      },
      {
        title: 'Document summaries',
        description:
          'Generate concise summaries of long contracts to enable quick review and triage. Focus deep review time on the contracts that matter most, not the ones that happen to be on top of the pile.',
      },
    ],
    caseStudySlugs: ['contract-price-escalation'],
    functionSlugs: ['contract-intelligence', 'ap-automation'],
    stats: [
      { value: '1,200+', label: 'Contracts parsed' },
      { value: '97%', label: 'Extraction accuracy' },
      { value: '$2.4M', label: 'Saved in unanticipated costs' },
    ],
    relatedLinks: [
      { title: 'Text Analytics', url: 'https://algoscale.com/text-analytics/', description: 'Extract insights from unstructured text in contracts and legal documents' },
      { title: 'Generative AI Services', url: 'https://algoscale.com/generative-ai-services/', description: 'Leverage generative AI for document understanding and analysis' },
      { title: 'Predictive Analytics', url: 'https://algoscale.com/predictive-data-analytics-services/', description: 'Predict contract risks and optimize procurement decisions' },
      { title: 'Custom Software Development', url: 'https://algoscale.com/custom-software-development-services/', description: 'Build custom integrations for your contract management workflow' },
    ],
  },
  {
    slug: 'financial-services',
    industry: 'Financial Services',
    headline: 'Accelerate document-heavy workflows from KYC to loan processing',
    description:
      'DocumentIQ extracts structured data from KYC documents, loan applications, and regulatory filings — with confidence scores and audit trails built in.',
    seoTitle: 'AI Financial Document Processing | Algoscale',
    seoDescription:
      'Automate financial workflows with AI-powered Enterprise Document Management. Process KYC, loan applications, and compliance documents faster with Algoscale.',
    heroDescription:
      'Financial services runs on documents that demand both speed and accuracy: identity documents for KYC, income statements and asset disclosures for loan applications, and regulatory filings that auditors will scrutinize years later. DocumentIQ handles the extraction with confidence scores on every field, a feedback loop that creates an auditable correction history, and Excel exports formatted for compliance reporting.',
    painPoints: [
      {
        title: 'KYC document processing',
        description:
          'Onboarding new clients requires extracting and verifying data from passports, utility bills, corporate registrations, and beneficial ownership documents — each in different formats and from different jurisdictions.',
      },
      {
        title: 'Loan application data extraction',
        description:
          'Loan officers manually pull income figures, employment details, asset values, and liability amounts from tax returns, bank statements, and pay stubs. The process is slow and introduces errors that delay underwriting.',
      },
      {
        title: 'Regulatory report parsing',
        description:
          'Compliance teams must extract specific data points from regulatory filings, annual reports, and disclosure documents — often under tight deadlines and across hundreds of entities.',
      },
      {
        title: 'Audit trail requirements',
        description:
          'Every extracted value must be traceable back to its source document and page. Manual processes lack the systematic traceability that auditors and regulators require.',
      },
    ],
    features: [
      {
        title: 'Multi-format support',
        description:
          'Extract data from PDFs, scanned documents, and Word files in a single project. Handle the format variety inherent in client-submitted documentation without separate pipelines per document type.',
      },
      {
        title: 'Confidence scores',
        description:
          'Every extracted value includes a confidence score from the LLM. Route low-confidence extractions to human review automatically, and focus reviewer time where it matters most.',
      },
      {
        title: 'Feedback loops for audit trails',
        description:
          'When a reviewer corrects an extracted value, the original value, correction, reviewer identity, and timestamp are all recorded. This creates the auditable correction history regulators expect.',
      },
      {
        title: 'Excel export for compliance',
        description:
          'Export extracted data with confidence scores, page references, and feedback status in structured Excel files ready for compliance reporting, regulatory submission, or integration with downstream systems.',
      },
    ],
    caseStudySlugs: [],
    functionSlugs: ['contract-intelligence', 'claims-processing', 'ap-automation'],
    stats: [
      { value: '10,000+', label: 'Documents processed monthly' },
      { value: '94%', label: 'First-pass extraction accuracy' },
      { value: '3x', label: 'Faster client onboarding' },
    ],
    relatedLinks: [
      { title: 'Generative AI in Banking', url: 'https://algoscale.com/generative-ai-in-banking/', description: 'How generative AI is transforming banking and financial services' },
      { title: 'Financial Software Development', url: 'https://algoscale.com/financial-software-development-services/', description: 'Build compliant financial software solutions' },
      { title: 'Data Governance Consulting', url: 'https://algoscale.com/data-governance-consulting-services/', description: 'Ensure data quality and compliance across extracted documents' },
      { title: 'Insurance Software Development', url: 'https://algoscale.com/insurance-software-development-services/', description: 'Automate claims processing and policy document extraction' },
    ],
  },
  // Contract Intelligence was previously listed here as an "industry"
  // but is really a cross-industry function. It lives in
  // src/data/functions.ts now; nginx 301-redirects the old
  // /solutions/contract-intelligence URL to
  // /solutions/function/contract-intelligence.
]

export const solutionIndustries = solutions.map((s) => ({
  slug: s.slug,
  industry: s.industry,
}))
